<template>
    <div v-if="displayIcon" 
        :class="rootClass" 
        @mouseleave="isLoggedIn && !isFirstView ? handleCollapse() : null"
        @focusout="isLoggedIn ? handleFocusOut($event) : null">
        <button :class="[`${rootClass}__trigger`, isLoggedIn && isExpanded ? `${rootClass}__trigger--active` : '']"
            @mouseenter="handleExpand"
            @click="isLoggedIn ? null : handleExpand"
            @keydown.enter.prevent="isLoggedIn ? handleExpand() : null"
            @keydown.space.prevent="isLoggedIn ? handleExpand() : null"
            :aria-expanded="isExpanded"
            aria-haspopup="dialog"
            :aria-label="'Coveo AI Information'"
            :tabindex="0">
            <slot name="trigger-content">
                <span :class="`${rootClass}__icon`">
                    <CoveoAiIcon />
                </span>
            </slot>
        </button>
        <Transition name="coveo-panel">
            <div v-if="isExpanded" :class="`${rootClass}__panel`" 
                @mouseenter="isLoggedIn ? handleExpand() : null">
                <div :class="`${rootClass}__panel-header`">
                    <p :class="`${rootClass}__panel-title`">
                        <slot name="panel-title"></slot>
                    </p>
                    <div :class="`${rootClass}__panel-actions`">
                        <span :class="`${rootClass}__panel-coveo-icon`">
                            <CoveoAiIcon />
                        </span>
                        <span :class="`${rootClass}__close`">
                            <CloseIcon @click="handleCollapse"
                                @keydown.enter.prevent="handleCollapse"
                                @keydown.space.prevent="handleCollapse"
                                tabindex="0"
                                role="button"
                                :aria-label="'Close'">
                            </CloseIcon>
                        </span>
                    </div>
                </div>
                <div :class="`${rootClass}__panel-content`">
                    <slot name="panel-content"></slot>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script>
import CoveoAiIcon from '../svg-elements/coveo-ai-icon/coveoAiIcon.vue'
import { CloseIcon } from '../svg-elements/close-icon'

export default {
    name: 'CoveoAINotification',
    components: {
        CoveoAiIcon,
        CloseIcon
    },
    props: {
        displayIcon: {
            type: Boolean,
            default: false
        },
        isCoveoExpanded: {
            type: Boolean,
            default: false
        },
        isFirstView: {
            type: Boolean,
            default: false
        },
        componentStyle: {
            type: String,
            default: 'loggedOut',
            validator: (value) => ['loggedIn', 'loggedOut'].includes(value)
        }
    },
    emits: ['update:isCoveoExpanded'],
    computed: {
        isExpanded() {
            return this.isCoveoExpanded
        },
        isLoggedIn() {
            return this.componentStyle === 'loggedIn'
        },
        rootClass() {
            return this.isLoggedIn ? 'coveo-logged-in-info' : 'coveo-logged-out-info'
        },
        triggerLabel() {
            return this.$slots['panel-title'] ? undefined : 'Coveo AI'
        }
    },
    methods: {
        handleExpand() {
            this.$emit('update:isCoveoExpanded', true)
        },
        handleCollapse() {
            this.$emit('update:isCoveoExpanded', false)
        },
        clickOutside(event) {
            if (this.isExpanded && this.$el && !this.$el.contains(event.target)) {
                this.handleCollapse()
            }
        },
        handleFocusOut(event) {
            const relatedTarget = event.relatedTarget
            if (!relatedTarget || !this.$el.contains(relatedTarget)) {
                this.handleCollapse()
            }
        },
        handleEscapeKey(event) {
            if (event.key === 'Escape' && this.isExpanded) {
                this.handleCollapse()
            }
        }
    },
    mounted() {
        document.addEventListener('click', this.clickOutside)
        document.addEventListener('keydown', this.handleEscapeKey)
    },
    beforeUnmount() {
        document.removeEventListener('click', this.clickOutside)
        document.removeEventListener('keydown', this.handleEscapeKey)
    }
}
</script>

<style lang="scss">
@import './coveoAINotification.scss';
</style>
